/**
 * A cabinet's outline in plan, edge by edge, with each edge's outward normal
 * and whether it is a FRONT (a door face) — what cabinet trim runs along.
 *
 * A box is its rotated rectangle. An L-shaped corner unit is the L its
 * renderer draws (`cornerCabinetRenderer`): in the unit's local frame the
 * wall corner is at (-w/2, -d/2), the A leg's door face runs along local X at
 * z = -d/2 + cornerBox, the B leg's along local Z at x = -w/2 + cornerBox, and
 * the two meet at the inside corner (-w/2 + cornerBox, -d/2 + cornerBox).
 *
 * Rotation convention (`under-cabinet-lighting/strips.js`): a local point
 * (lx, lz) is at world (cx + lx·cos ry + lz·sin ry, cz − lx·sin ry + lz·cos ry),
 * so local +Z — the door's facing — is world (sin ry, cos ry). Pure.
 */
const { isPointInsidePolygon } = require('../geometry/pointInPolygon');
const { angledEndLocalOutline } = require('../cabinet-runs/angledEnds');

const DEFAULT_CORNER_BOX_M = 24 * 0.0254;
const EDGE_EPS_M = 1e-4;

const toWorld = (o) => {
  const ry = o.rotation?.y || 0;
  const c = Math.cos(ry);
  const s = Math.sin(ry);
  return ([lx, lz]) => ({
    x: o.center.x + lx * c + lz * s,
    z: o.center.z - lx * s + lz * c,
  });
};

const isLCorner = (o) =>
  o?.type === 'corner-cabinet' &&
  o.dimensions?.cornerVariant !== 'single-sided';

// a box's rectangle between two local x's, its front (edge 2) a door face
const rect = (x0, x1, hz) => ({
  points: [
    [x0, -hz],
    [x1, -hz],
    [x1, hz],
    [x0, hz],
  ],
  fronts: new Set([2]),
});

/**
 * The outline's local polygons — one for a box or an L corner unit, the two
 * side panels for a fridge column (`fridge-surround`, whose box spans the
 * fridge opening between them: the over-fridge cabinet carries the crown
 * there — or the header panel standing for the opening behind it — and
 * treating the box as solid drew it twice) — each with the edges
 * (edge i runs from vertex i to vertex i+1) that are door faces.
 */
function localOutlines(o) {
  const hx = o.dimensions.x / 2;
  const hz = o.dimensions.z / 2;
  // an angled end is its cut outline (`angledEnds.js`): its straight front
  // and its angled face are door faces — the edges facing the room
  if (o.category?.angledEnd && o.angledEndSide) {
    const points = angledEndLocalOutline(o).map((p) => [p.x, p.z]);
    const next = (i) => points[(i + 1) % points.length];
    const area = points.reduce(
      (a, [x0, z0], i) => a + x0 * next(i)[1] - next(i)[0] * z0,
      0
    );
    const fronts = new Set();
    points.forEach(([x0, z0], i) => {
      const [x1, z1] = next(i);
      const dx = (x1 - x0) / Math.hypot(x1 - x0, z1 - z0);
      // the outward normal's z: (dz, −dx) turning one way round, (−dz, dx)
      // the other
      if ((area > 0 ? -dx : dx) > 0.5) fronts.add(i);
    });
    return [{ points, fronts }];
  }
  if (isLCorner(o)) {
    const cb = o.dimensions.cornerBox || DEFAULT_CORNER_BOX_M;
    return [
      {
        points: [
          [-hx, -hz],
          [hx, -hz],
          [hx, -hz + cb],
          [-hx + cb, -hz + cb],
          [-hx + cb, hz],
          [-hx, hz],
        ],
        // the A leg's face (vertex 2 → 3) and the B leg's (3 → 4)
        fronts: new Set([2, 3]),
      },
    ];
  }
  // the header panel over a fridge (`fridgeHeader.js`) stands for the
  // opening behind it, from its face back to the wall, as the over-fridge
  // cabinet's box does: the crown runs across its face and the side panels'
  // inner faces are covered
  if (o.category?.fridgeHeader && o.columnDepth > 2 * hz) {
    const back = hz - o.columnDepth;
    return [
      {
        points: [
          [-hx, back],
          [hx, back],
          [hx, hz],
          [-hx, hz],
        ],
        fronts: new Set([2]),
      },
    ];
  }
  const panel = o.dimensions.sidePanelThickness;
  if (o.type === 'fridge-surround' && panel > 0 && panel < hx) {
    return [rect(-hx, -hx + panel, hz), rect(hx - panel, hx, hz)];
  }
  return [rect(-hx, hx, hz)];
}

/** The outline polygons in world x/z (`[]` without usable geometry). */
function carrierOutlines(o) {
  if (!(o?.dimensions?.x > 0) || !(o?.dimensions?.z > 0) || !o.center)
    return [];
  const at = toWorld(o);
  return localOutlines(o).map(({ points }) => points.map(at));
}

/**
 * Each outline edge `{ a, b, normal, front }`: `normal` the unit vector out
 * of the cabinet, `front` true for a door face.
 */
function outlineEdges(o) {
  if (!(o?.dimensions?.x > 0) || !(o?.dimensions?.z > 0) || !o.center)
    return [];
  const at = toWorld(o);
  return localOutlines(o).flatMap(({ points, fronts }) => {
    const polygon = points.map(at);
    return polygon.map((a, i) => {
      const b = polygon[(i + 1) % polygon.length];
      const len = Math.hypot(b.x - a.x, b.z - a.z);
      let normal = { x: (b.z - a.z) / len, z: -(b.x - a.x) / len };
      const probe = {
        x: (a.x + b.x) / 2 + normal.x * EDGE_EPS_M,
        z: (a.z + b.z) / 2 + normal.z * EDGE_EPS_M,
      };
      if (isPointInsidePolygon(probe, polygon))
        normal = { x: -normal.x, z: -normal.z };
      return { a, b, normal, front: fronts.has(i) };
    });
  });
}

module.exports = { carrierOutlines, outlineEdges };
